import React, { useState } from 'react';
import { Smile, Zap, ShieldCheck, Heart, AlarmClock } from 'lucide-react';

function WhyChooseUs() {
  const [activeReason, setActiveReason] = useState(0);

  const reasons = [
    { 
      icon: <Smile />, 
      title: "Personalized Treatment", 
      desc: "Each plan is tailored to your unique smile, needs, and goals.",
      details: [
        "Comprehensive initial consultation",
        "Custom treatment planning",
        "Individualized care approach",
        "Prioritized communication and follow-ups", 
        "Focus on long-term outcomes over quick fixes"
      ]
    },
    { 
      icon: <Zap />, 
      title: "Modern Technology", 
      desc: "Digital X-rays, 3D scans, and advanced tools for efficient care.",
      details: [
        "Digital X-rays with low radiation",
        "3D imaging for detailed diagnostics",
        "Intraoral cameras for real-time patient education",
        "Laser dentistry for minimally invasive procedures",
        "Same-day crowns with digital impressions"
      ]
    },
    { 
      icon: <ShieldCheck />, 
      title: "Comfort-First Care", 
      desc: "From gentle techniques to calming amenities—we prioritize your comfort.",
      details: [
        "Calming environment with soothing amenities",
        "Gentle techniques for stress-free procedures",
        "Sedation options for anxious patients",
        "Friendly, no-judgment approach",
        "Shorter wait times and attentive scheduling"
      ]
    },
    { 
      icon: <Heart />, 
      title: "Compassionate Team", 
      desc: "Friendly, skilled professionals that make you feel at home.",
      details: [
        "Experienced dentists & hygienists",
        "Warm, welcoming front desk staff",
        "Dedicated to patient education and communication",
        "Multilingual team members (if applicable)",
        "Passionate about creating positive dental experiences"
      ]
    },
    { 
      icon: <AlarmClock />, 
      title: "Same-Day Emergencies", 
      desc: "Urgent dental needs? We're here when it matters most.",
      details: [
        "Priority scheduling for urgent issues",
        "Walk-in welcome for emergencies",
        "Treatment for toothaches, trauma, lost fillings, etc.",
        "Immediate pain relief and stabilization",
        "Follow-up planning for long-term restoration"
      ]
    }
  ];

  return (
    <section className="relative bg-gradient-to-br from-teal-50 to-blue-50 py-20 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">
        {/* Left Side - Navigation */}
        <div className="w-full lg:w-1/3 space-y-4">
          <h2 className="text-4xl font-bold text-gray-800 mb-8">
            Why Choose Pristine Smile
          </h2>
          <div className="space-y-4">
            {reasons.map((reason, index) => (
              <button
                key={index}
                onClick={() => setActiveReason(index)}
                className={`
                  w-full text-left p-4 rounded-xl transition-all duration-300
                  flex items-center space-x-4
                  ${activeReason === index 
                    ? 'bg-teal-600 text-white shadow-lg' 
                    : 'bg-white text-gray-700 hover:bg-teal-50'}
                `}
              >
                <div className={`
                  p-3 rounded-full
                  ${activeReason === index 
                    ? 'bg-white/20' 
                    : 'bg-teal-100 text-teal-600'}
                `}>
                  {React.cloneElement(reason.icon, { size: 24 })}
                </div>
                <span className="font-semibold">{reason.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Right Side - Active Reason Details */}
        <div className="w-full lg:w-2/3 bg-white rounded-2xl shadow-2xl p-10 min-h-[400px] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-600 to-lime-500"></div>
          
          <div className="relative z-10">
            <div className="flex items-center mb-6">
              <div className="bg-teal-100 text-teal-600 p-4 rounded-full mr-6">
                {React.cloneElement(reasons[activeReason].icon, { size: 40 })}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {reasons[activeReason].title}
                </h3>
                <p className="text-gray-600">
                  {reasons[activeReason].desc}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {reasons[activeReason].details.map((detail, index) => (
                <div 
                  key={index} 
                  className="flex items-center space-x-4 animate-fade-in"
                >
                  <div className="w-4 h-4 bg-teal-500 rounded-full"></div>
                  <p className="text-gray-700">{detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WhyChooseUs;